import {
  Box,
  Divider,
  Grid,
  createStyles,
  makeStyles,
  Menu,
  MenuItem,
  Typography,
} from '@material-ui/core'
import MenuIcon from '@mui/icons-material/Menu'
import React, { useState } from 'react'
import {
  DateField,
  EditButton,
  FunctionField,
  ReferenceField,
  sanitizeFetchType,
  Show,
  ShowProps,
  Tab,
  TabbedShowLayout,
  TextField,
  TitleProps,
  useMutation,
  useNotify,
  useQuery,
  useRefresh,
  useShowContext,
  useTranslate,
} from 'react-admin'
import { Authority } from '../../../../core/auth/Authority'
import { ResourceName } from '../../../../core/ResourceName'
import { useHasAuthority } from '../../../hooks/useHasAuthority'
import { DeviceCategoryExtendedDto } from '../../../../core/dto/device-category/device-category-extended.dto'
import { CameraDto } from '../../../../core/dto/device/camera/cameras/camera.dto'
import { DeviceStatusesTab } from '../../common/modules/ModuleStatus'
import { DeviceTypes } from '../../../../core/enum/DeviceTypes'
import { SelfPOSAccessTicketCashlessMachineDto } from '../../../../core/dto/device/ticket-machines/selfpos-access-ticket-cashless-machine/selfpos-access-ticket-cashless-machine.dto'
import Button from '../../../common/customized-mui-components/Button'
import {
  AnnouncementsDialog,
  SetAutomatAPICredentialsDialog,
} from '../iksoris-parking-ticket-cash-machines/IksorisParkingTicketCashMachineDialogs'
import { ConfirmDialog } from '../../../common/ConfirmDialog'
import { BlankActions } from '../../../common/BlankActions'

const useStyles = makeStyles(() =>
  createStyles({
    machineActions: {
      right: '25px',
      position: 'absolute',
      whiteSpace: 'nowrap',
    },
  }),
)

interface SelfPOSAccessTicketCashlessMachineTitleProps extends TitleProps {
  record?: SelfPOSAccessTicketCashlessMachineDto
}

const ShowTitle = (data: SelfPOSAccessTicketCashlessMachineTitleProps) => {
  const { record: terminal } = data
  const translate = useTranslate()
  return (
    <span>
      {translate(
        'resources.selfpos-access-ticket-cashless-machines.titles.show',
      )}
      : {terminal?.id}. {terminal?.name}
    </span>
  )
}

const GeneralActions = () => {
  const { record: ticketMachine } =
    useShowContext<SelfPOSAccessTicketCashlessMachineDto>()
  const hasAuthority = useHasAuthority()

  return (
    <>
      <Divider />
      <Box p={1}>
        <Grid container direction="row" justify="flex-end" alignItems="center">
          <Grid item>
            <EditButton
              basePath={`/${ResourceName.SELFPOS_ACCESS_TICKET_CASHLESS_MACHINES}`}
              record={ticketMachine}
              variant="contained"
              style={{ margin: '2px' }}
              disabled={
                !hasAuthority(
                  Authority.EDIT_SELFPOS_ACCESS_TICKET_CASHLESS_MACHINES,
                )
              }
            />
          </Grid>
        </Grid>
      </Box>
    </>
  )
}

export const SelfPOSAccessTicketCashlessMachineTabs = ({ ...props }) => {
  const { simpleView, createdAt, ...rest } = props
  const { record: ticketMachine } = rest
  const translate = useTranslate()
  const hasAuthority = useHasAuthority()
  const [mutate] = useMutation()
  const classes = useStyles()
  const [anchorEl, setAnchorEl] = React.useState(null)
  const [automatAPICredentialsModalOpen, setAutomatAPICredentialsModalOpen] =
    useState(false)
  const [announcementsModalOpen, setAnnouncementsModalOpen] = useState(false)
  const [
    removeAutomatAPICredentialsDialogOpen,
    setRemoveAutomatAPICredentialsDialogOpen,
  ] = useState(false)
  const refresh = useRefresh()
  const notify = useNotify()

  const { data: serverDate } = useQuery(
    {
      type: sanitizeFetchType('getServerDateTime'),
      resource: ResourceName.CURRENT_USER,
      payload: {},
    },
    {
      enabled: !simpleView && !createdAt,
    },
  )

  const handleClick = (event: any) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleRequest = async (id: number, fetchType: string) => {
    await mutate(
      {
        type: sanitizeFetchType(fetchType),
        resource: ResourceName.SELFPOS_ACCESS_TICKET_CASHLESS_MACHINES,
        payload: { ticketMachineId: id },
      },
      {
        returnPromise: true,
      },
    )
  }

  const handleSetAutomatAPICredentialsDialog = () => {
    setAutomatAPICredentialsModalOpen(true)
  }

  const handleRemoveAutomatAPICredentials = async () => {
    setRemoveAutomatAPICredentialsDialogOpen(false)
    await mutate(
      {
        type: sanitizeFetchType('removeAutomatAPICredentials'),
        resource: ResourceName.SELFPOS_ACCESS_TICKET_CASHLESS_MACHINES,
        payload: {
          ticketMachineId: ticketMachine?.id,
        },
      },
      {
        returnPromise: true,
        onFailure: (err) => notify(err?.message, 'error'),
      },
    )
    refresh()
  }

  const handleRemoveAutomatAPICredentialsDialog = () => {
    setRemoveAutomatAPICredentialsDialogOpen(true)
  }

  const handleAnnouncementsDialog = () => {
    setAnnouncementsModalOpen(true)
  }

  return (
    <TabbedShowLayout {...rest} syncWithLocation={!simpleView}>
      <Tab label="resources.selfpos-access-ticket-cashless-machines.tabs.general">
        <div className={classes.machineActions}>
          {hasAuthority(
            Authority.SELFPOS_ACCESS_TICKET_CASHLESS_MACHINES_ACTIONS_LIST,
          ) && (
            <Button
              onClick={handleClick}
              label="const.selectAction"
              variant="contained"
            >
              <MenuIcon />
            </Button>
          )}
          <Menu
            anchorEl={anchorEl}
            keepMounted
            open={Boolean(anchorEl)}
            onClose={handleClose}
            onClick={handleClose}
          >
            {hasAuthority(
              Authority.SELFPOS_ACCESS_TICKET_CASHLESS_MACHINES_MANAGE_ANNOUNCEMENTS_BUTTON,
            ) && (
              <MenuItem {...props} onClick={handleAnnouncementsDialog}>
                <Typography>
                  {translate('resources.ticket-machines.actions.announcements')}
                </Typography>
              </MenuItem>
            )}
            <AnnouncementsDialog
              open={announcementsModalOpen}
              close={() => setAnnouncementsModalOpen(false)}
              resource={ResourceName.SELFPOS_ACCESS_TICKET_CASHLESS_MACHINES}
            />
            {hasAuthority(
              Authority.SELFPOS_ACCESS_TICKET_CASHLESS_MACHINES_LOCK_BUTTON,
            ) && (
              <MenuItem
                {...props}
                onClick={() => handleRequest(props?.record?.id, 'lock')}
              >
                <Typography>
                  {translate(
                    'resources.selfpos-access-ticket-cashless-machines.actions.lock',
                  )}
                </Typography>
              </MenuItem>
            )}
            {hasAuthority(
              Authority.SELFPOS_ACCESS_TICKET_CASHLESS_MACHINES_UNLOCK_BUTTON,
            ) && (
              <MenuItem
                {...props}
                onClick={() => handleRequest(props?.record?.id, 'unlock')}
              >
                <Typography>
                  {translate(
                    'resources.selfpos-access-ticket-cashless-machines.actions.unlock',
                  )}
                </Typography>
              </MenuItem>
            )}
            {hasAuthority(
              Authority.SELFPOS_ACCESS_TICKET_CASHLESS_MACHINES_RESTART_BUTTON,
            ) && (
              <MenuItem
                {...props}
                onClick={() => handleRequest(props?.record?.id, 'restart')}
              >
                <Typography>
                  {translate(
                    'resources.selfpos-access-ticket-cashless-machines.actions.restart',
                  )}
                </Typography>
              </MenuItem>
            )}
            {hasAuthority(
              Authority.SELFPOS_ACCESS_TICKET_CASHLESS_MACHINES_REBOOT_DEVICE_BUTTON,
            ) && (
              <MenuItem
                {...props}
                onClick={() => handleRequest(props?.record?.id, 'rebootDevice')}
              >
                <Typography>
                  {translate(
                    'resources.selfpos-access-ticket-cashless-machines.actions.reboot-device',
                  )}
                </Typography>
              </MenuItem>
            )}
            {hasAuthority(
              Authority.SELFPOS_ACCESS_TICKET_CASHLESS_MACHINES_SHUTDOWN_DEVICE_BUTTON,
            ) && (
              <MenuItem
                {...props}
                onClick={() =>
                  handleRequest(props?.record?.id, 'shutDownDevice')
                }
              >
                <Typography>
                  {translate(
                    'resources.selfpos-access-ticket-cashless-machines.actions.shutdown-device',
                  )}
                </Typography>
              </MenuItem>
            )}
            {hasAuthority(
              Authority.SELFPOS_ACCESS_TICKET_CASHLESS_MACHINES_MANAGE_CREDENTIALS_BUTTON,
            ) && (
              <MenuItem
                {...props}
                onClick={handleSetAutomatAPICredentialsDialog}
              >
                <Typography>
                  {translate(
                    'resources.ticket-machines.actions.setAutomatAPICredentials',
                  )}
                </Typography>
              </MenuItem>
            )}
            <SetAutomatAPICredentialsDialog
              open={automatAPICredentialsModalOpen}
              close={() => setAutomatAPICredentialsModalOpen(false)}
              resource={ResourceName.SELFPOS_ACCESS_TICKET_CASHLESS_MACHINES}
            />
            {hasAuthority(
              Authority.SELFPOS_ACCESS_TICKET_CASHLESS_MACHINES_MANAGE_CREDENTIALS_BUTTON,
            ) && (
              <MenuItem
                {...props}
                onClick={handleRemoveAutomatAPICredentialsDialog}
              >
                <Typography>
                  {translate(
                    'resources.ticket-machines.actions.removeAutomatAPICredentials',
                  )}
                </Typography>
              </MenuItem>
            )}
            {hasAuthority(
              Authority.SELFPOS_ACCESS_TICKET_CASHLESS_MACHINES_PRINT_TEST_TICKET_BUTTON,
            ) && (
              <MenuItem
                {...props}
                onClick={() =>
                  handleRequest(props?.record?.id, 'printTestTicket')
                }
              >
                <Typography>
                  {translate(
                    'resources.selfpos-access-ticket-cashless-machines.actions.print-test-ticket',
                  )}
                </Typography>
              </MenuItem>
            )}
            <ConfirmDialog
              open={removeAutomatAPICredentialsDialogOpen}
              confirm={handleRemoveAutomatAPICredentials}
              source="setAutomatAPICredentials"
              cancel={() => setRemoveAutomatAPICredentialsDialogOpen(false)}
              action="remove"
              resource={ResourceName.SELFPOS_ACCESS_TICKET_CASHLESS_MACHINES}
            />
          </Menu>
        </div>
        <TextField source="id" label="ID" />
        <TextField source="name" />
        <FunctionField<SelfPOSAccessTicketCashlessMachineDto>
          source="isWatched"
          render={(record?: SelfPOSAccessTicketCashlessMachineDto) => (
            <div>
              {record?.isWatched
                ? translate('const.yes')
                : translate('const.no')}
            </div>
          )}
        />
        <DateField
          source="lastRefresh"
          showTime
          options={{
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
          }}
        />
        {ticketMachine?.categoryId &&
          hasAuthority(Authority.VIEW_DEVICE_CATEGORIES) && (
            <ReferenceField
              source="categoryId"
              reference={ResourceName.DEVICE_CATEGORIES}
              link="show"
            >
              <FunctionField<DeviceCategoryExtendedDto>
                source="hierarchyString"
                render={(record?: DeviceCategoryExtendedDto) =>
                  `${record?.id}. ${record?.hierarchyString}`
                }
              />
            </ReferenceField>
          )}
        {ticketMachine?.cameraId && hasAuthority(Authority.VIEW_CAMERAS) && (
          <ReferenceField
            source="cameraId"
            reference={ResourceName.CAMERAS}
            link="show"
          >
            <FunctionField<CameraDto>
              source="name"
              render={(camera?: CameraDto) =>
                `${camera?.id}. ${camera?.description}`
              }
            />
          </ReferenceField>
        )}
        <TextField source="address" />
        <TextField source="automatAPIAddress" />
        {(!simpleView && <GeneralActions />) || <BlankActions />}
      </Tab>
      <Tab
        label="resources.selfpos-access-ticket-cashless-machines.tabs.statuses"
        path="statuses"
      >
        {ticketMachine?.jsonStatus && (
          <DeviceStatusesTab
            jsonStatus={ticketMachine.jsonStatus}
            deviceType={DeviceTypes.SELFPOS_ACCESS_TICKET_CASHLESS_MACHINE}
            referenceDateTime={createdAt || serverDate?.serverDateTime}
          />
        )}
        <BlankActions />
      </Tab>
    </TabbedShowLayout>
  )
}

export const SelfPOSAccessTicketCashlessMachineShow = (props: ShowProps) => (
  <Show {...props} actions={false} title={<ShowTitle />}>
    <SelfPOSAccessTicketCashlessMachineTabs />
  </Show>
)
